<template>
  <div class="container">
    <div class="row">
      <div class="col">
        {{ message }}
      </div>
      <data-form2 :data="expData" :formFields="formFields" @changed="inputChanged" :readonly="true" minheight="380px" />
    </div>
    <div class="row">
      <div class="col">
        <div class="actions">

          <button type="button" class="btn btn-success bi bi-download" @click="clickOk">
            Export
          </button>
          <button type="button" class="btn btn-danger bi bi-x" @click="closeModal">
            Zatvori
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataForm2 from "../../widgets/DataForm2.vue";
import dateUtils from "../../../utils/date-common";
import invoiceDataService from "../../../services/invoice.service";
import * as XLSX from "xlsx";

export default {
  name: "export-data",
  components: {
    DataForm2,
  },
  props: {
    endpoint: {
      type: String,
      required: true,
      default: "",
    },
    message: {
      type: String,
      required: false,
      default: "",
    },
  },
  data() {
    return {
      jsonData: [],
    };
  },
  computed: {
    formFields() {
      return {
        rows: [
          [{ id: { type: "hidden" } }],
          [
            {
              from: { label: "Od datuma", type: "date" },
            },
            { to: { label: "Do datuma", type: "date" } },
            {},
            {},
          ],
        ],
      };
    },
    expData() {
      return {
        from: dateUtils.getFirstDayOfsMonth().toISOString().substring(0, 10),
        to: new Date().toISOString().substring(0, 10),
      };
    },
  },
  methods: {
    inputChanged(e, name) {
      this.expData[name] = e;
    },
    closeModal() {
      this.$store.commit("app/hideModal");
    },
    async exportExcelData() {
      const data = this.jsonData['data'];
      const columns = this.jsonData['columns'];
      const fileName = "Exported_data.xlsx";

      const header = columns.map(c => c.label);
      const rows = data.map(row => {
        return columns.map(col => {
          let val = row[col.field];
          if (col.field.includes('.')) {
            val = col.field.split('.').reduce((acc, part) => acc && acc[part], row);
          }
          if (col.dataFormat) {
            return col.dataFormat(val);
          }
          return val;
        });
      });

      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);

      // Calculate optimal column widths
      const colWidths = header.map((h, i) => {
        let max = h.length;
        rows.forEach(row => {
          const cellValue = row[i] ? String(row[i]) : "";
          if (cellValue.length > max) {
            max = cellValue.length;
          }
        });
        return { wch: max + 2 }; // Add a little padding
      });
      ws['!cols'] = colWidths;

      // Apply formatting
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell_address = { c: C, r: R };
          const cell_ref = XLSX.utils.encode_cell(cell_address);
          if (!ws[cell_ref]) continue;

          // Skip header row
          if (R === 0) continue;

          if (ws[cell_ref].t === 'n') {
            // Format all numbers to 2 decimals, except for ID
            if (columns[C].field !== 'id') {
              ws[cell_ref].z = "0.00";
            }
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Export");
      XLSX.writeFile(wb, fileName);
    },
    async clickOk() {
      if (this.endpoint == 'sellings') {
        console.log("Exporting sellings data...");
        invoiceDataService
          .getFromToSell("Normal", this.expData.from, this.expData.to)
          .then((res) => {
            this.jsonData["columns"] = [
              {
                label: "Id",
                field: "id",
              },
              {
                label: "Marka",
                field: "Selling.brand",
              },
              {
                label: "Model",
                field: "Selling.model",
              },
              {
                label: "Registracija",
                field: "Selling.registration_nr",
              },
              {
                label: "Godina proizvodnje",
                field: "Selling.year",
              },
              {
                label: "Šasija",
                field: "Selling.chassis_nr",
              },
              {
                label: "Kupac",
                field: "Customer.name",
              },
              {
                label: "Lizing partner",
                field: "Selling.leasing",
              },
              {
                label: "Korisnik lizinga",
                field: "Selling.leasing_user",
              },
              {
                label: "Datum Fakture",
                field: "doc_date",
                dataFormat: dateUtils.formatDate,
              },
              {
                label: "Broj fakture",
                field: "doc_nr",
              },
              {
                label: "Broj avansne uplate",
                field: "advance_doc_nr",
              },
              {
                label: "Ukupan iznos BRUTO EUR",
                field: "iznos_exc",
              },
              {
                label: "Ukupan iznos NETO RSD",
                field: "osn_exc",
              },
              {
                label: "Leasing - Otkupna vrednost na dan prodaje EUR",
                field: "otkupna_vrednost",
              },
              {
                label: "Neto vrednost na dan prodaje EUR",
                field: "bb_neto_eur",
              },
              {
                label: "Neto razlika Knjigovodstvena - Prodajna EUR",
                field: "kg_prodajna_vrednost",
              },
              {
                label: "Neto razlika Prodajna - Lizing EUR",
                field: "prodajna_otkupna_vrednost",
              },
            ];
            this.jsonData["data"] = res.data.map((i) => {
              let mult = i.invoice_status == "Deleted" ? -1 : 1;
              const iznos_exc = mult * i.invoice_items.reduce((accumulator, item) => {
                return Number.parseFloat(
                  Number.parseFloat(accumulator) +
                  Number.parseFloat(item.price_exc) +
                  Number.parseFloat(item.tax_exc)
                ).toFixed(2);
              }, 0);
              const iznos = mult * i.invoice_items.reduce((accumulator, item) => {
                return Number.parseFloat(
                  Number.parseFloat(accumulator) +
                  Number.parseFloat(item.price) +
                  Number.parseFloat(item.tax)
                ).toFixed(2);
              }, 0);
              const osn = mult * i.invoice_items
                .filter((itm) => Number.parseFloat(itm.tax) > 0)
                .reduce((accumulator, item) => {
                  return Number.parseFloat(
                    Number.parseFloat(accumulator) + Number.parseFloat(item.price) //+
                    // Number.parseFloat(item.tax)
                  ).toFixed(2);
                }, 0);
              const osn_exc = mult * i.invoice_items
                .filter((itm) => Number.parseFloat(itm.tax) > 0)
                .reduce((accumulator, item) => {
                  return Number.parseFloat(
                    Number.parseFloat(accumulator) + Number.parseFloat(item.price_exc) //+
                    // Number.parseFloat(item.tax)
                  ).toFixed(2);
                }, 0);
              const empty = "";
              const kg_prodajna_vrednost = i.Selling.kg_vrednost ? i.Selling.kg_vrednost - i.Selling.bb_neto_eur : empty;
              const prodajna_otkupna_vrednost = i.Selling.otkupna_vrednost ? i.Selling.bb_neto_eur - i.Selling.otkupna_vrednost : empty;

              // Find Advance invoice
              let advance_doc_nr = "";
              if (i.Selling && i.Selling.invoices) {
                // Filter for Advance invoices that are 'Done' (finished) and not 'Deleted' (cancelled)
                const advanceInvoices = i.Selling.invoices
                  .filter(inv => inv.invoice_type === 'Advance' && inv.invoice_status === 'Done')
                  .sort((a, b) => b.id - a.id); // Sort by ID descending to get the last one

                if (advanceInvoices.length > 0) {
                  advance_doc_nr = advanceInvoices[0].doc_nr;
                }
              }

              const otkupna_vrednost = Number(i.Selling.otkupna_vrednost);
              const bb_neto_eur = Number(i.Selling.bb_neto_eur);

              return { ...i, iznos_exc: Number(iznos_exc), iznos: Number(iznos), osn: Number(osn), osn_exc: Number(osn_exc), empty, kg_prodajna_vrednost, prodajna_otkupna_vrednost, advance_doc_nr, otkupna_vrednost, bb_neto_eur };
            });

            setTimeout(() => {
              this.exportExcelData();
            }, 200);
          });
        return;
      }
      invoiceDataService
        .getFromTo("Normal", this.expData.from, this.expData.to)
        .then((res) => {
          this.jsonData["columns"] = [
            {
              label: "Id",
              field: "id",
            },
            {
              label: "Datum Fakture",
              field: "doc_date",
              dataFormat: dateUtils.formatDate,
            },
            {
              label: "Valuta",
              field: "exchange",
            },
            {
              label: "Outbound",
              field: "Reservation.reservation_num",
            },
            {
              label: "Outbound status",
              field: "Reservation.status",
            },
            {
              label: "Poslovnica",
              field: "Station.code",
            },
            {
              label: "Kupac",
              field: "Customer.name",
            },
            {
              label: "Država",
              field: "Reservation.Country.name",
            },
            {
              label: "ITS Broj",
              field: "additional.its_no",
            },
            {
              label: "Ulazna faktura",
              field: "additional.incoming_invoice",
            },
            {
              label: "Broj faktura",
              field: "doc_nr",
            },
            {
              label: "Ukupan iznos EUR",
              field: "iznos_exc",
            },
            {
              label: "Ukupan iznos RSD",
              field: "iznos",
            },
            {
              label: "Osnovica PDV RSD",
              field: "osn",
            },
            {
              label: "PDV RSD",
              field: "pdv",
            },
            {
              label: "Ukuona provizija RSD Sa PDV",
              field: "prov",
            },
          ];
          this.jsonData["data"] = res.data.map((i) => {
            let mult = i.invoice_status == "Deleted" ? -1 : 1;
            const iznos_exc = mult * i.invoice_items.reduce((accumulator, item) => {
              return Number.parseFloat(
                Number.parseFloat(accumulator) +
                Number.parseFloat(item.price_exc) +
                Number.parseFloat(item.tax_exc)
              ).toFixed(2);
            }, 0);
            const iznos = mult * i.invoice_items.reduce((accumulator, item) => {
              return Number.parseFloat(
                Number.parseFloat(accumulator) +
                Number.parseFloat(item.price) +
                Number.parseFloat(item.tax)
              ).toFixed(2);
            }, 0);
            const osn = mult * i.invoice_items
              .filter((itm) => Number.parseFloat(itm.tax) > 0)
              .reduce((accumulator, item) => {
                return Number.parseFloat(
                  Number.parseFloat(accumulator) + Number.parseFloat(item.price) //+
                  // Number.parseFloat(item.tax)
                ).toFixed(2);
              }, 0);
            const pdv = mult * i.invoice_items.reduce((accumulator, item) => {
              return Number.parseFloat(
                Number.parseFloat(accumulator) + Number.parseFloat(item.tax)
              ).toFixed(2);
            }, 0);
            const prov = 1 * (
              Number.parseFloat(osn) + Number.parseFloat(pdv)
            ).toFixed(2);
            return { ...i, iznos_exc: Number(iznos_exc), iznos: Number(iznos), osn: Number(osn), pdv: Number(pdv), prov: Number(prov) };
          });
          const sum = {
            doc_nr: "UKUPNO:",
            iznos_exc: 0,
            iznos: 0,
            osn: 0,
            pdv: 0,
            prov: 0,
          };

          for (let index = 0; index < this.jsonData["data"].length; index++) {
            const el = this.jsonData["data"][index];
            sum.iznos_exc = (
              Number.parseFloat(sum.iznos_exc) + Number.parseFloat(el.iznos_exc)
            ).toFixed(2);
            sum.iznos = (
              Number.parseFloat(sum.iznos) + Number.parseFloat(el.iznos)
            ).toFixed(2);
            sum.osn = (
              Number.parseFloat(sum.osn) + Number.parseFloat(el.osn)
            ).toFixed(2);
            sum.pdv = (
              Number.parseFloat(sum.pdv) + Number.parseFloat(el.pdv)
            ).toFixed(2);
            sum.prov = (
              Number.parseFloat(sum.prov) + Number.parseFloat(el.prov)
            ).toFixed(2);
          }

          // Convert summations to numbers
          sum.iznos_exc = Number(sum.iznos_exc);
          sum.iznos = Number(sum.iznos);
          sum.osn = Number(sum.osn);
          sum.pdv = Number(sum.pdv);
          sum.prov = Number(sum.prov);

          this.jsonData["data"].push(sum);

          setTimeout(() => {
            this.exportExcelData();
          }, 200);
        });
    },
  },
};
</script>

<style>
/* .vuejs3-datepicker__calendar {
      position: fixed !important;
    } */
</style>